import React, { Fragment, useState, useMemo } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Search, Layout } from 'lucide-react';
import { SizeTier, CHART_DEFINITIONS, ChartCategory } from '../../lib/api/templates';

const CATEGORIES: (ChartCategory | 'All')[] = [
    'All',
    'Financial',
    'Decision',
    'Operations',
    'Customer Behavior',
    'Marketing',
    'Growth',
    'Risk',
    'Prediction',
    'Expert',
    'Other'
];

interface AddChartModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddChart: (type: string, sizeTier: SizeTier, aspectRatio: string) => void;
    onDragStart?: (type: string, sizeTier: SizeTier, aspectRatio: string) => void;
    onDragEnd?: () => void;
}


export const AddChartModal: React.FC<AddChartModalProps> = ({
    isOpen,
    onClose,
    onAddChart,
    onDragStart,
    onDragEnd
}) => {
    const [selectedCategory, setSelectedCategory] = useState<ChartCategory | 'All'>('All');
    const [searchQuery, setSearchQuery] = useState('');

    const filteredCharts = useMemo(() => {
        return CHART_DEFINITIONS.filter(chart => {
            const matchesCategory = selectedCategory === 'All' || chart.categories.includes(selectedCategory);
            const matchesSearch = chart.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (chart.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);
            return matchesCategory && matchesSearch;
        });
    }, [selectedCategory, searchQuery]);

    const getPreviewColor = (i: number) => {
        const colors = [
            "bg-blue-50 border-blue-100 text-blue-500",
            "bg-emerald-50 border-emerald-100 text-emerald-500",
            "bg-violet-50 border-violet-100 text-violet-500",
            "bg-amber-50 border-amber-100 text-amber-500",
            "bg-rose-50 border-rose-100 text-rose-500",
            "bg-indigo-50 border-indigo-100 text-indigo-500"
        ];
        return colors[i % colors.length];
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-5xl transform transition-all">
                                <div className="bg-white rounded-2xl shadow-2xl h-[85vh] flex flex-col overflow-hidden">
                                    {/* Header */}
                                    <div className="px-6 pt-5 pb-4 border-b border-neutral-200 flex-shrink-0">
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <Dialog.Title as="h2" className="text-lg font-semibold text-neutral-900">
                                                    Add Chart
                                                </Dialog.Title>
                                                <p className="text-sm text-neutral-500 mt-0.5">
                                                    Select a chart to add to your template
                                                </p>
                                            </div>
                                            <button
                                                onClick={onClose}
                                                className="text-neutral-400 hover:text-neutral-600 p-2 rounded-lg hover:bg-neutral-100 transition-all"
                                            >
                                                <X size={20} />
                                            </button>
                                        </div>

                                        {/* Search */}
                                        <div className="relative mb-4">
                                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                                            <input
                                                type="text"
                                                placeholder="Search charts..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2 text-sm border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                                            />
                                        </div>

                                        {/* Category Tabs */}
                                        <div className="flex gap-1 overflow-x-auto pb-1">
                                            {CATEGORIES.map(cat => (
                                                <button
                                                    key={cat}
                                                    onClick={() => setSelectedCategory(cat)}
                                                    className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${selectedCategory === cat
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                                                        }`}
                                                >
                                                    {cat}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 overflow-y-auto p-4">
                                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                                            {filteredCharts.map((chart, idx) => (
                                                <div
                                                    key={chart.type}
                                                    className="group relative flex flex-col bg-white border border-neutral-200 rounded-xl cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all duration-200 overflow-hidden"
                                                    draggable={true}
                                                    onDragStart={(e) => {
                                                        // Firefox hack
                                                        e.dataTransfer.setData("text/plain", chart.type);
                                                        e.dataTransfer.effectAllowed = "copy";
                                                        onDragStart?.(chart.type, chart.defaultSizeTier, chart.defaultAspectRatio);
                                                    }}
                                                    onDragEnd={() => {
                                                        onDragEnd?.();
                                                    }}
                                                    onClick={() => {
                                                        onAddChart(chart.type, chart.defaultSizeTier, chart.defaultAspectRatio);
                                                    }}
                                                >
                                                    {/* Preview Area */}
                                                    <div className={`h-20 flex items-center justify-center relative overflow-hidden ${getPreviewColor(idx)}`}>
                                                        <div className="transform group-hover:scale-110 transition-transform duration-300">
                                                            <Layout size={28} />
                                                        </div>

                                                        {/* Size indicator */}
                                                        <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-white/90 rounded text-[10px] font-medium text-neutral-600 uppercase">
                                                            {chart.defaultSizeTier}
                                                        </div>
                                                    </div>

                                                    {/* Content */}
                                                    <div className="p-3">
                                                        <h3 className="text-sm font-medium text-neutral-800 group-hover:text-blue-600 transition-colors truncate">
                                                            {chart.title}
                                                        </h3>
                                                        <p className="text-[11px] text-neutral-500 line-clamp-2 mt-0.5">
                                                            {chart.description}
                                                        </p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {filteredCharts.length === 0 && (
                                            <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
                                                <Layout size={40} className="mb-3 opacity-30" />
                                                <p className="text-sm font-medium">No charts found</p>
                                                <p className="text-xs">Try adjusting your search or category</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
